// Hover / pinned detail panels for nodes and edges.
import type { JsonAudit, JsonEdge, JsonFlow, JsonNode } from './serialize';
import { icon } from './icons';
import { HUGE, beltText, effClass, flowLines, flowText, fmt, h, isGeneric, itemName, pct, shortBelt, flowNodes } from './util';

const KIND_LABEL: Record<string, string> = {
  machine: 'Machine',
  generator: 'Generator',
  splitter: 'Splitter',
  smartSplitter: 'Smart / programmable splitter',
  merger: 'Merger',
  storage: 'Storage',
  fluidNetwork: 'Pipe network',
  externalIn: 'Blueprint input',
  externalOut: 'Blueprint output',
  other: 'Other',
};

function kv(label: string, value: string | Node | null | undefined): HTMLElement | null {
  if (value === null || value === undefined || value === '') return null;
  return h('div', { class: 'kv' }, h('span', { class: 'k' }, label), h('span', { class: 'v num' }, value));
}

function flowTable(a: JsonAudit, rows: { item: string; want?: number; got?: number; limiting?: boolean }[]): HTMLElement {
  return h(
    'table',
    { class: 'mini' },
    h('thead', null, h('tr', null, h('th', null, 'item'), h('th', { class: 'num' }, 'gets/min'), h('th', { class: 'num' }, 'needs/min'))),
    h(
      'tbody',
      null,
      ...rows.map((r) =>
        h(
          'tr',
          { class: r.limiting ? 'limiting' : '' },
          h('td', null, icon(r.item, 16), ' ', itemName(a, r.item), r.limiting ? h('span', { class: 'tag bad' }, 'limiting') : null),
          h('td', { class: 'num' }, r.got === undefined ? '—' : fmt(r.got)),
          h('td', { class: 'num' }, r.want === undefined ? '—' : fmt(r.want)),
        ),
      ),
    ),
  );
}

export function nodeDetails(a: JsonAudit, n: JsonNode): HTMLElement {
  const box = h('div', { class: 'details' });
  box.append(h('div', { class: 'd-title' }, icon(n.kind === 'externalIn' ? n.item : n.className || undefined, 22), ' ', n.label, h('span', { class: 'tag' }, KIND_LABEL[n.kind] ?? n.kind)));
  if (n.buildingName && n.buildingName !== n.label) box.append(kv('Building', n.buildingName)!);
  if (n.count && n.count > 1) {
    box.append(kv('Combined', `${n.count} identical machines (totals shown)`)!);
    if (n.effRange && n.effRange[1] - n.effRange[0] > 1e-4) box.append(kv('Efficiency range', `${pct(n.effRange[0])} – ${pct(n.effRange[1])}`)!);
  }

  if (n.kind === 'machine' || n.kind === 'generator') {
    const cls = effClass(n.efficiency, n.kind === 'generator' || !!n.recipe);
    if (n.kind === 'generator') {
      box.append(kv('Fuel', n.fuelName ?? 'unknown')!);
      if (n.powerMW) box.append(kv('Power', `${fmt(n.powerMW)} MW`)!);
    } else {
      box.append(kv('Recipe', n.recipeName ?? h('span', { class: 'bad' }, 'NO RECIPE'))!);
    }
    if (n.paused) box.append(kv('State', h('span', { class: 'tag' }, 'paused — switched off, treated as absent'))!);
    {
      const circuit = n.circuitId ? a.power.circuits.find((c) => c.id === n.circuitId) : undefined;
      const ids = n.members ?? [n.id];
      const unpowered = ids.filter((id) => a.power.unpoweredNodeIds.includes(id)).length;
      if (circuit) box.append(kv('Power', `${circuit.label} · ${fmt(circuit.consumptionMW)} MW drawn on it`)!);
      else if (unpowered) box.append(kv('Power', h('span', { class: 'bad' }, unpowered === ids.length ? 'not connected to power' : `${unpowered} of ${ids.length} not connected to power`))!);
    }
    if (Math.abs(n.clock - 1) > 1e-6) box.append(kv('Clock', `${fmt(n.clock * 100)}%`)!);
    if (n.outputMultiplier !== 1) box.append(kv('Somersloop', `×${fmt(n.outputMultiplier)} output`)!);
    const effEl = h('span', { class: `eff-text ${cls}` }, pct(n.efficiency));
    box.append(kv('Efficiency', effEl)!);
    if (Math.abs(n.idealEfficiency - n.efficiency) > 1e-4) box.append(kv('With unlimited belts', pct(n.idealEfficiency))!);
    if (n.efficiency < 0.999) {
      const why: string[] = [];
      if (n.inputFrac < 0.999) why.push(`input-limited (${pct(n.inputFrac)})`);
      if (n.outputFrac < 0.999) why.push(`output backs up (downstream takes ${pct(n.outputFrac)})`);
      if (why.length) box.append(kv('Cause', why.join('; '))!);
    }
    const rows: { item: string; want?: number; got?: number; limiting?: boolean }[] = [];
    const seen = new Set<string>();
    for (const [item, want] of Object.entries(n.demand ?? {})) {
      seen.add(item);
      rows.push({ item, want, got: n.supplied[item] ?? 0, limiting: n.limitingItems.includes(item) });
    }
    for (const [item, got] of Object.entries(n.supplied)) if (!seen.has(item)) rows.push({ item, got });
    if (rows.length) {
      box.append(h('div', { class: 'd-sub' }, 'Inputs'));
      box.append(flowTable(a, rows));
    }
    if (n.production && Object.keys(n.production).length) {
      box.append(h('div', { class: 'd-sub' }, 'Produces (at 100%)'));
      box.append(h('div', { class: 'num' }, ...flowNodes(a, n.production, icon)));
      if (n.efficiency < 0.999) {
        const actual: JsonFlow = {};
        for (const [k, v] of Object.entries(n.production)) actual[k] = v * n.efficiency;
        box.append(h('div', { class: 'muted num' }, `actual: ${flowText(a, actual)}`));
      }
    }
  } else if (n.kind === 'externalIn') {
    const badge = n.sushi?.length
      ? h('span', { class: 'tag mixed' }, `sushi belt? merges with ${n.sushi.map((k) => itemName(a, k)).join(', ')}`)
      : isGeneric(n.item)
        ? h('span', { class: 'tag warn' }, 'unknown')
        : h('span', { class: 'tag' }, 'inferred');
    box.append(kv('Item', h('span', null, icon(n.item, 16), ' ', n.item ? itemName(a, n.item) : '?', ' ', badge))!);
    const inp = a.inputs.find((i) => i.nodeId === n.id);
    if (inp) {
      box.append(kv('Belt', beltText(inp.belt, inp.capacity))!);
      box.append(kv('Required', `${fmt(inp.required)}/min`)!);
      box.append(kv('Used', inp.used >= HUGE ? `${fmt(inp.required)}/min + rest passes through` : `${fmt(inp.used)}/min`)!);
      if (inp.passThrough) box.append(kv('Pass-through', 'yes: excess is forwarded to an output')!);
    }
  } else if (n.kind === 'externalOut') {
    const out = a.outputs.find((o) => o.nodeId === n.id);
    if (out) {
      box.append(kv('Belt', beltText(out.belt, out.capacity))!);
      box.append(kv('Delivers', out.passThrough ? `pass-through ${Object.keys(out.flow).filter((k) => k !== '*').map((k) => itemName(a, k)).join(' + ')}` : flowText(a, out.flow))!);
      const ideal = flowLines(a, out.idealFlow).join(' + ');
      if (!out.passThrough && ideal !== flowLines(a, out.flow).join(' + ')) box.append(kv('With faster belts', ideal)!);
    }
  } else {
    if (Object.keys(n.supplied).length) box.append(kv('Throughput', flowText(a, n.supplied))!);
    if (n.rules?.length) {
      box.append(h('div', { class: 'd-sub' }, 'Rules'));
      box.append(h('ul', { class: 'plain' }, ...n.rules.map((r) => h('li', null, `output ${r.outputIndex + 1}: ${r.itemName}`))));
    }
  }
  if (Object.keys(n.wrongItems).length) box.append(kv('Wrong items', h('span', { class: 'bad num' }, flowText(a, n.wrongItems)))!);
  if (n.notes.length) box.append(h('ul', { class: 'plain notes' }, ...n.notes.map((t) => h('li', null, t))));

  const connected = n.ports.filter((p) => p.edgeId).length;
  if (n.ports.length) {
    const ins = n.ports.filter((p) => p.dir === 'in');
    const outs = n.ports.filter((p) => p.dir === 'out');
    const portStr = (ps: typeof n.ports) => `${ps.filter((p) => p.edgeId).length}/${ps.length}`;
    box.append(kv('Ports used', `${connected}/${n.ports.length} (in ${portStr(ins)}, out ${portStr(outs)})`)!);
  }
  if (n.position) box.append(h('div', { class: 'muted small num' }, `pos ${fmt(n.position.x / 100)}, ${fmt(n.position.y / 100)}, ${fmt(n.position.z / 100)} m`));
  return box;
}

export function edgeDetails(a: JsonAudit, e: JsonEdge): HTMLElement {
  const byId = new Map(a.nodes.map((n) => [n.id, n]));
  const backedNote = e.backedUp
    ? h('div', { class: 'd-note' }, 'Belt is full and backed up: upstream would send more, but downstream only consumes the rate shown. At a splitter the surplus goes to its other outputs; otherwise it backs up further.')
    : null;
  const from = byId.get(e.fromNode)?.label ?? e.fromNode;
  const to = byId.get(e.toNode)?.label ?? e.toNode;
  const box = h('div', { class: 'details' });
  const tags: HTMLElement[] = [];
  if (e.bottleneck) tags.push(h('span', { class: 'tag bad' }, 'bottleneck'));
  else if (e.saturated) tags.push(h('span', { class: 'tag warn' }, 'saturated'));
  if (e.passThrough) tags.push(h('span', { class: 'tag' }, 'pass-through'));
  if (e.backedUp) tags.push(h('span', { class: 'tag' }, 'backed up'));
  if (e.mixed) tags.push(h('span', { class: 'tag mixed' }, 'sushi / mixed belt'));
  box.append(h('div', { class: 'd-title' }, `${from} → ${to}`, ...tags));
  if (backedNote) box.append(backedNote);
  box.append(kv('Type', e.kind === 'direct' ? 'direct connection' : e.fluid ? 'pipe' : 'belt')!);
  box.append(kv('Capacity', e.capacity === null ? 'unlimited' : `${fmt(e.capacity)}/min${e.limitingSegment ? ` (${shortBelt(e.limitingSegment)})` : ''}`)!);
  const flowEl = e.passThrough
    ? h('span', null, 'pass-through ', ...Object.keys(e.flow).filter((k) => k !== '*').flatMap((k, i) => [i ? ' + ' : '', icon(k, 16), ' ', itemName(a, k)]))
    : h('span', null, ...flowNodes(a, e.flow, icon));
  box.append(kv('Flow', flowEl)!);
  const idealStr = flowText(a, e.idealFlow);
  if (!e.passThrough && idealStr !== flowText(a, e.flow)) box.append(kv('Ideal flow', `${idealStr} (with unlimited belts)`)!);
  if (Object.keys(e.offer).length) box.append(kv('Offered', flowText(a, e.offer))!);
  if (Object.keys(e.accept).length) box.append(kv('Accepted', Object.entries(e.accept).map(([k, v]) => (v >= HUGE ? `any ${itemName(a, k)}` : `${fmt(v)}/min ${itemName(a, k)}`)).join(' + '))!);
  if (e.segments.length) {
    box.append(h('div', { class: 'd-sub' }, `${e.segments.length} segment${e.segments.length === 1 ? '' : 's'}`));
    const total = e.segments.reduce((s, x) => s + (x.lengthM ?? 0), 0);
    box.append(
      h(
        'ol',
        { class: 'segments' },
        ...e.segments.map((s) =>
          h(
            'li',
            { class: s.label === e.limitingSegment && s.capacity === e.capacity ? 'limiting' : '' },
            h('span', null, s.label),
            h('span', { class: 'num muted' }, `${s.capacity === null ? '∞' : fmt(s.capacity) + '/min'}${s.lengthM !== undefined ? ` · ${fmt(s.lengthM)} m` : ''}`),
          ),
        ),
      ),
    );
    if (total > 0) box.append(h('div', { class: 'muted small num' }, `total length ${fmt(total)} m`));
  }
  box.append(h('div', { class: 'muted small' }, `${e.from} → ${e.to}`));
  return box;
}
